import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getTranscripcionById,
  getAudioTranscripcion,
} from '../../../../services/clientService';
import CallChat2 from '../../../../components/Chat2/CallChat2';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { clientsDetailI18n } from '../i18n';
import './callDetail.css';

const CallDetail = ({ DesignSystemComponents }) => {
  const { Button, Card, Input, Badge } = DesignSystemComponents;
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useScopedTranslation(clientsDetailI18n);

  const [callData, setCallData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const abortController = new AbortController();

    const fetchCallDetail = async () => {
      if (!id) {
        setError(t('cd_call_error_invalid_id'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const data = await getTranscripcionById(id, abortController.signal);
        setCallData(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message || t('cd_call_error_load'));
          console.error('Error fetching call detail:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCallDetail();

    return () => abortController.abort();
  }, [id]);

  const handleLoadAudio = async () => {
    try {
      const blob = await getAudioTranscripcion(id);
      const url = window.URL.createObjectURL(blob);
      setAudioUrl(url);
    } catch (error) {
      console.error('Error cargando audio:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('cd_call_na');
    return new Date(dateString).toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className='container-client-detail'>
        <p className='caption'>{t('cd_call_loading')}</p>
      </div>
    );
  }

  if (error || !callData) {
    return (
      <div className='container-client-detail'>
        <Button
          variant='naked'
          iconStart='chevronLeft'
          label={t('cd_back')}
          size='sm'
          onClick={() => navigate(-1)}
        />
        <p className='caption text-danger mt-3'>
          {error || t('cd_call_not_found')}
        </p>
      </div>
    );
  }

  return (
    <div className='container client-detail'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        <Button
          variant='naked'
          iconStart='chevronLeft'
          label={t('cd_back')}
          size='sm'
          onClick={() => navigate(-1)}
        />
      </div>

      <div className='row'>
        <div className='calldetail-container'>
          <div className='col-6'>
            <Card specialPadding='p-4'>
              <div className='mb-4'>
                <h2 className='fw-medium mb-1'>{t('cd_call_title')}</h2>
                <p className='caption font-light mb-0'>
                  {t('cd_call_id')} {callData.id}
                </p>
              </div>
              <div>
                {[
                  {
                    label: t('cd_call_date'),
                    value: callData.date
                      ? formatDate(callData.date)
                      : t('cd_call_na'),
                  },
                  { label: t('cd_call_agent_id'), value: callData.agentId },
                  {
                    label: t('cd_call_agent_name'),
                    value: callData.greetingAgentName,
                  },
                  {
                    label: t('cd_call_customer_phone'),
                    value: callData.customerPhone,
                  },
                  {
                    label: t('cd_call_feeling'),
                    render: callData.feeling ? (
                      <Badge
                        text={
                          callData.feeling === 'Positive'
                            ? t('cd_call_feeling_positive')
                            : callData.feeling === 'Negative'
                              ? t('cd_call_feeling_negative')
                              : callData.feeling
                        }
                        color={
                          callData.feeling === 'Positive'
                            ? 'success'
                            : callData.feeling === 'Negative'
                              ? 'danger'
                              : 'light'
                        }
                        size='sm'
                      />
                    ) : (
                      <span className='caption font-light'>
                        {t('cd_call_na')}
                      </span>
                    ),
                  },
                  { label: t('cd_call_greeting'), value: callData.greeting },
                  { label: t('cd_call_silence'), value: callData.silence },
                  { label: t('cd_call_channel'), value: callData.channel },
                  { label: t('cd_call_operation'), value: callData.operacion },
                ].map((item) => (
                  <div
                    key={item.label}
                    className='d-flex justify-content-between mb-2'
                  >
                    <p className='caption fw-semibold'>{item.label}</p>
                    {item.render || (
                      <p className='caption'>
                        {item.value === null ||
                        item.value === undefined ||
                        item.value === ''
                          ? t('cd_call_na')
                          : item.value}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {callData.summary && (
                <>
                  <hr className='line mt-4 mb-3' />
                  <h3 className='fw-bold mb-3'>{t('cd_call_summary')}</h3>
                  <div className='content-break summary-break'>
                    <p className='body '>{callData.summary}</p>
                  </div>
                </>
              )}
            </Card>
          </div>
          <div className='col-6'>
            <Card notPadding>
              <div className='px-4 pt-4'>
                <div className='d-flex justify-content-center mb-3'>
                  {audioUrl ? (
                    <audio controls className='w-100' src={audioUrl} />
                  ) : (
                    <Button
                      variant='primary'
                      label={t('cd_call_download_audio')}
                      size='sm'
                      onClick={handleLoadAudio}
                    />
                  )}
                </div>
                <Input
                  icon='search'
                  name='searchAudio'
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('cd_call_search_placeholder')}
                  type='text'
                  value={searchTerm}
                />
              </div>
              <hr className='line my-0' />
              <div className='content-break chat-break'>
                <CallChat2 callData={callData} searchQuery={searchTerm} />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallDetail;
